/**
 * Alert sounds, synthesised rather than loaded.
 *
 * A handful of oscillators costs nothing, ships nothing, and cannot 404 — where
 * audio files would be assets to host, cache-bust and keep out of the service
 * worker. It also means a sound can be described here as what it IS (two notes,
 * a fifth apart, decaying fast) rather than as a binary nobody can inspect.
 */

export type SoundName = 'none' | 'chime' | 'bell' | 'blip' | 'knock' | 'rise';

/** Order shown in settings. `none` first, since silence is a real choice. */
export const SOUND_NAMES: readonly SoundName[] = ['none', 'chime', 'bell', 'blip', 'knock', 'rise'];

interface Note {
  /** Hz. */
  freq: number;
  /** Seconds after the sound starts. */
  at: number;
  /** Seconds. */
  dur: number;
  type?: OscillatorType;
  /** Peak gain before the shared volume is applied. */
  gain?: number;
}

const PRESETS: Record<Exclude<SoundName, 'none'>, Note[]> = {
  // A fifth, the second note landing while the first still rings.
  chime: [
    { freq: 880, at: 0, dur: 0.5 },
    { freq: 1318.5, at: 0.08, dur: 0.55 },
  ],
  // One note plus its octave at a third of the level, which is roughly what
  // makes a struck bell sound struck rather than beeped.
  bell: [
    { freq: 1046.5, at: 0, dur: 0.9 },
    { freq: 2093, at: 0, dur: 0.55, gain: 0.33 },
  ],
  blip: [{ freq: 660, at: 0, dur: 0.12, type: 'square', gain: 0.5 }],
  // Low and very short: a knock is felt more than heard, and a long low note
  // just sounds like a fault.
  knock: [
    { freq: 180, at: 0, dur: 0.09, type: 'triangle' },
    { freq: 120, at: 0.05, dur: 0.1, type: 'triangle', gain: 0.7 },
  ],
  rise: [
    { freq: 523.25, at: 0, dur: 0.16 },
    { freq: 784, at: 0.09, dur: 0.16 },
    { freq: 1046.5, at: 0.18, dur: 0.3 },
  ],
};

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;

function context(): AudioContext | null {
  if (ctx) return ctx;
  const Ctx: Ctor | undefined =
    window.AudioContext ?? (window as { webkitAudioContext?: Ctor }).webkitAudioContext;
  if (!Ctx) return null;
  ctx = new Ctx();
  return ctx;
}

/*
 * Browsers start an AudioContext suspended until the page has been interacted
 * with, and an alert fires on a timer rather than on a click — so the first one
 * would be silent with no indication why. Unlocking on the first gesture of the
 * session means by the time anything fires, the context is already running.
 */
if (typeof window !== 'undefined') {
  const unlock = () => {
    void context()?.resume();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

/**
 * Play one of the presets.
 *
 * Never throws and never blocks: audio is a nicety on top of an alert that is
 * already on screen, so a browser that refuses to play it should cost nothing.
 *
 * @param volume 0..1, applied on top of each note's own gain.
 */
export function playSound(name: SoundName, volume = 0.6): void {
  if (name === 'none' || volume <= 0) return;

  try {
    const audio = context();
    if (!audio) return;
    void audio.resume();

    const start = audio.currentTime + 0.01;
    for (const note of PRESETS[name]) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = note.type ?? 'sine';
      osc.frequency.value = note.freq;

      const peak = Math.max(0.0001, (note.gain ?? 1) * volume * 0.3);
      const t0 = start + note.at;
      // A few milliseconds of attack, because jumping straight to peak gain
      // puts a click at the front of every note.
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + note.dur);

      osc.connect(gain).connect(audio.destination);
      osc.start(t0);
      osc.stop(t0 + note.dur + 0.02);
    }
  } catch {
    /* No audio available — the alert card is still on screen. */
  }
}
